import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Heart,
  Clock,
  AlertTriangle,
  Syringe,
  Activity,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  Zap,
  CheckCircle2,
  XCircle
} from 'lucide-react';

import { API_URL } from '../config';

export default function SCASTPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('algorithme');
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('sca-st-plus');
      return saved ? JSON.parse(saved) : {
        debutDouleur: '',
        delaiECG: '',
        poids: '',
        age: '',
        contrindications: [],
        territoireECG: '',
        traitement: '',
        amplitudeA: '',
        amplitudeB: ''
      };
    } catch {
      return {
        debutDouleur: '',
        delaiECG: '',
        poids: '',
        age: '',
        contrindications: [],
        territoireECG: '',
        traitement: '',
        amplitudeA: '',
        amplitudeB: ''
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('sca-st-plus', JSON.stringify(formData));
  }, [formData]);

  const handleReset = () => {
    if (window.confirm('Réinitialiser le formulaire ?')) {
      localStorage.removeItem('sca-st-plus');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `SCA-ST-Plus-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-sca-st`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleCI = (ci) => {
    setFormData(prev => ({
      ...prev,
      contrindications: prev.contrindications.includes(ci)
        ? prev.contrindications.filter(c => c !== ci)
        : [...prev.contrindications, ci]
    }));
  };

  const territoiresECG = [
    { derivations: 'V1-V3', territoire: 'Antéroseptal', artere: 'IVA moyenne' },
    { derivations: 'V3-V4', territoire: 'Apical', artere: 'IVA moyenne' },
    { derivations: 'V1-V4', territoire: 'Antérieur', artere: 'IVA moyenne' },
    { derivations: 'DI-VL', territoire: 'Latéral haut', artere: 'IVA/Circonflexe' },
    { derivations: 'V5-V6', territoire: 'Latéral bas', artere: 'Circonflexe' },
    { derivations: 'DII-DIII-VF', territoire: 'Inférieur', artere: 'CD/Circonflexe' }
  ];

  const contrindications = [
    { cat: 'Cardiaque', items: ['Choc cardiogénique', 'ACR', 'Dissection aortique', 'HTA réfractaire (>180/110)'] },
    { cat: 'Hémorragique', items: ['Saignement actif', 'Chirurgie <3 sem', 'Traumatisme majeur <3 sem'] },
    { cat: 'Neurologique', items: ['AVC/AIT <6 mois', 'TC <3 sem', 'Hémorragie intracrânienne'] },
    { cat: 'Autres', items: ['Ulcère gastroduodénal', 'Insuffisance hépatique sévère', 'Grossesse', 'Anticoagulant oral'] }
  ];

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-5xl mx-auto">
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 md:w-8 md:h-8 text-red-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">SCA ST+</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Syndrome Coronarien Aigu avec sus-décalage ST</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-red-400/10 border-red-400/30 p-2">
          <Zap className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>Urgence vitale :</strong> Délai ECG-Coronarographie &lt; 120 min
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-[hsl(var(--bg-elev))]">
            <TabsTrigger value="algorithme" className="text-xs md:text-sm">Algorithme</TabsTrigger>
            <TabsTrigger value="traitements" className="text-xs md:text-sm">Traitements</TabsTrigger>
            <TabsTrigger value="territoires" className="text-xs md:text-sm">Territoires ECG</TabsTrigger>
            <TabsTrigger value="sgarbossa" className="text-xs md:text-sm">BBG Sgarbossa</TabsTrigger>
          </TabsList>

          <TabsContent value="algorithme" className="space-y-3 mt-3">
            <Card className="border-cyan-400/30 bg-cyan-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  ÉVALUATION INITIALE
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Début de la douleur (heures)</label>
                  <Input
                    type="number"
                    value={formData.debutDouleur}
                    onChange={(e) => setFormData(prev => ({ ...prev, debutDouleur: e.target.value }))}
                    placeholder="Heures depuis début douleur"
                    className="h-9 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Délai ECG → Coronarographie (min)</label>
                  <Input
                    type="number"
                    value={formData.delaiECG}
                    onChange={(e) => setFormData(prev => ({ ...prev, delaiECG: e.target.value }))}
                    placeholder="Minutes"
                    className="h-9 text-sm"
                  />
                </div>

                {formData.debutDouleur && formData.delaiECG && (
                  <Alert className={`${parseFloat(formData.delaiECG) <= 120 ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'} p-2`}>
                    <AlertDescription className="text-xs font-medium">
                      {parseFloat(formData.debutDouleur) < 12 && parseFloat(formData.delaiECG) <= 120 ? (
                        <span className="text-green-300">✅ ANGIOPLASTIE PRIMAIRE recommandée</span>
                      ) : parseFloat(formData.debutDouleur) < 12 && parseFloat(formData.delaiECG) > 120 ? (
                        <span className="text-red-300">⚠️ FIBRINOLYSE à envisager (délai &gt; 120 min)</span>
                      ) : (
                        <span className="text-amber-300">⚠️ Délai &gt; 12h : Angioplastie primaire</span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="traitements" className="space-y-3 mt-3">
            <Card className="border-purple-400/30">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                  <Syringe className="w-4 h-4" />
                  TRAITEMENTS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <Alert className="bg-blue-500/10 border-blue-500/30 p-3">
                  <AlertDescription className="text-xs">
                    <strong className="text-blue-300">2 stratégies :</strong><br/>
                    • ANGIOPLASTIE PRIMAIRE (si délai &lt; 120 min)<br/>
                    • FIBRINOLYSE (si délai &gt; 120 min)
                  </AlertDescription>
                </Alert>

                <Card className="border-green-500/30 bg-green-500/5">
                  <CardHeader className="p-2">
                    <div className="text-xs font-semibold text-green-300">ANGIOPLASTIE PRIMAIRE</div>
                  </CardHeader>
                  <CardContent className="p-2 space-y-2">
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-xs font-medium text-cyan-300 mb-1">ANTICOAGULATION</div>
                      <Badge className="bg-cyan-500/20 text-cyan-300 font-mono text-xs">ENOXAPARINE 0,5 mg/kg IVD</Badge>
                    </div>
                    
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-xs font-medium text-purple-300 mb-1">BI-ANTIAGREGATION</div>
                      <div className="space-y-2">
                        <Badge className="bg-blue-500/20 text-blue-300 font-mono text-xs block w-full p-2">ASPIRINE 250 mg IV</Badge>
                        <div className="text-xs text-gray-400 mb-1">+ UN INHIBITEUR P2Y12 PO :</div>
                        
                        <div className="p-2 rounded bg-purple-500/10 border border-purple-500/30">
                          <div className="text-xs font-medium text-purple-300 mb-1">Option 1 : PRASUGREL (Efient®) 60 mg</div>
                          <div className="text-xs text-gray-400">Si ACR récupéré attendu en coro</div>
                          <div className="text-xs text-red-300 mt-1">CI : NACO/AVK, ATCD AVC/AIT, &gt;75 ans, cirrhose Child C</div>
                        </div>
                        
                        <div className="p-2 rounded bg-purple-500/10 border border-purple-500/30">
                          <div className="text-xs font-medium text-purple-300 mb-1">Option 2 : TICAGRELOR (Brilique®) 180 mg</div>
                          <div className="text-xs text-gray-400">Alternative si CI Prasugrel</div>
                          <div className="text-xs text-red-300 mt-1">CI : NACO/AVK, Ins. hépatique sévère, ATCD hémorragie IC, BAV, BC, SCA ST+ inf</div>
                        </div>
                        
                        <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30">
                          <div className="text-xs font-medium text-amber-300 mb-1">Option 3 : CLOPIDOGREL (Plavix®) 600 mg</div>
                          <div className="text-xs text-gray-400">2ème intention</div>
                          <div className="text-xs text-amber-300 mt-1">Si CI PRASUGREL & TICAGRELOR ou haut risque hémorragique</div>
                        </div>
                      </div>
                    </div>

                    <Alert className="bg-cyan-500/10 border-cyan-500/30 p-2">
                      <AlertDescription className="text-xs text-cyan-300">
                        <strong>Note :</strong> Si patient sous CLOPIDOGREL, privilégier changement pour PRASUGREL ou TICAGRELOR
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <div className="text-xs font-semibold text-purple-300 mb-2 pt-3 border-t border-gray-700">FIBRINOLYSE (si délai &gt; 120 min)</div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[hsl(var(--text-dim))] mb-1 block">Poids (kg)</label>
                    <Input
                      type="number"
                      value={formData.poids}
                      onChange={(e) => setFormData(prev => ({ ...prev, poids: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--text-dim))] mb-1 block">Âge (ans)</label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-400/30">
                  <div className="text-xs font-semibold text-blue-300 mb-2">ASPIRINE</div>
                  <Badge className="bg-blue-500/20 text-blue-300 font-mono">250 mg IV</Badge>
                </div>

                <div className="p-3 rounded-lg bg-green-500/10 border border-green-400/30">
                  <div className="text-xs font-semibold text-green-300 mb-2">ENOXAPARINE (Lovenox®)</div>
                  {formData.age && (
                    <Badge className="bg-green-500/20 text-green-300 font-mono">
                      {parseInt(formData.age) < 75 ? '30mg IVD + 1mg/kg SC (max 100mg)' : '0,75mg/kg SC uniquement (max 75mg)'}
                    </Badge>
                  )}
                </div>

                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-400/30">
                  <div className="text-xs font-semibold text-purple-300 mb-2">CLOPIDOGREL (Plavix®)</div>
                  {formData.age && (
                    <Badge className="bg-purple-500/20 text-purple-300 font-mono">
                      {parseInt(formData.age) < 75 ? '300 mg' : '75 mg'}
                    </Badge>
                  )}
                </div>

                {formData.poids && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30">
                    <div className="text-xs font-semibold text-red-300 mb-2">TNK-tPA (Fibrinolyse)</div>
                    <Badge className="bg-red-500/20 text-red-300 font-mono">
                      {parseInt(formData.poids) < 60 ? '30 mg (6 ml)' :
                       parseInt(formData.poids) < 70 ? '35 mg (7 ml)' :
                       parseInt(formData.poids) < 80 ? '40 mg (8 ml)' :
                       parseInt(formData.poids) < 90 ? '45 mg (9 ml)' : '50 mg (10 ml)'}
                    </Badge>
                    <div className="text-[10px] text-red-300 mt-1">Bolus IV unique en 10 secondes</div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-red-400/30 bg-red-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-red-300 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  CONTRE-INDICATIONS FIBRINOLYSE
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <Accordion type="multiple">
                  {contrindications.map((group, idx) => (
                    <AccordionItem key={idx} value={`ci-${idx}`}>
                      <AccordionTrigger className="text-xs md:text-sm">{group.cat}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1">
                          {group.items.map((item, i) => (
                            <label key={i} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.contrindications.includes(item)}
                                onChange={() => toggleCI(item)}
                                className="rounded"
                              />
                              <span className="text-xs text-[hsl(var(--text))]">{item}</span>
                            </label>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                {formData.contrindications.length > 0 && (
                  <Alert className="mt-3 bg-red-500/20 border-red-500/40 p-2">
                    <AlertTriangle className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-xs text-red-300">
                      <strong>{formData.contrindications.length} CI identifiée(s)</strong> - Fibrinolyse contre-indiquée
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="territoires" className="space-y-3 mt-3">
            <Card className="border-amber-400/30 bg-amber-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-amber-300">TERRITOIRES ECG & ARTÈRES</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-2">
                  {territoiresECG.map((t, idx) => (
                    <div
                      key={idx}
                      onClick={() => setFormData(prev => ({ ...prev, territoireECG: t.derivations }))}
                      className={`p-2 rounded-lg cursor-pointer border transition-all ${
                        formData.territoireECG === t.derivations
                          ? 'bg-amber-500/20 border-amber-400/50'
                          : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-xs font-mono">{t.derivations}</Badge>
                        {formData.territoireECG === t.derivations && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                      </div>
                      <div className="text-xs text-[hsl(var(--text))]">{t.territoire}</div>
                      <div className="text-xs text-[hsl(var(--text-dim))]">→ {t.artere}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sgarbossa" className="space-y-3 mt-3">
            <Card className="border-blue-400/30 bg-blue-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-blue-300">SYNDROME CORONARIEN AIGU & BBG</CardTitle>
                <p className="text-xs text-[hsl(var(--text-dim))] mt-1">Critères de Sgarbossa-Smith modifiés</p>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                {/* CRITÈRE A */}
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-green-300">CRITÈRE A DE SGARBOSSA</div>
                    <Badge className="bg-green-500/20 text-green-300 text-xs">Meilleur critère</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--text))] mb-2">
                    <strong>Concordance inappropriée positive</strong>
                  </div>
                  <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                    <div className="text-xs text-[hsl(var(--text-dim))]">
                      Sus-décalage ST <strong className="text-green-300">&gt; 1mm</strong> dans les dérivations avec <strong>QRS positif</strong>
                    </div>
                  </div>
                </div>

                {/* CRITÈRE B */}
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-400/30">
                  <div className="text-xs font-semibold text-amber-300 mb-2">CRITÈRE B DE SGARBOSSA</div>
                  <div className="text-xs text-[hsl(var(--text))] mb-2">
                    <strong>Concordance inappropriée négative</strong>
                  </div>
                  <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                    <div className="text-xs text-[hsl(var(--text-dim))]">
                      Sous-décalage ST <strong className="text-amber-300">&gt; 1mm</strong> dans les dérivations avec <strong>QRS négatif</strong> (V1-V2-V3)
                    </div>
                  </div>
                </div>

                {/* CRITÈRE C */}
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-400/30">
                  <div className="text-xs font-semibold text-purple-300 mb-2">CRITÈRE C DE SGARBOSSA MODIFIÉ PAR SMITH</div>
                  <div className="text-xs text-[hsl(var(--text))] mb-2">
                    <strong>Discordance extrême</strong> (en l'absence d'instabilité HD ou d'OAP)
                  </div>
                  
                  <div className="space-y-2">
                    <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                      <div className="text-xs font-medium text-purple-300 mb-1">Pour SOUS-décalage ST :</div>
                      <div className="text-xs text-[hsl(var(--text-dim))]">
                        Si <strong className="text-purple-300">B/A &gt; 0,30</strong> → IDM probable
                      </div>
                      <div className="text-[10px] text-green-400 mt-1">Sensibilité 100%, Spécificité 88%</div>
                    </div>

                    <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                      <div className="text-xs font-medium text-purple-300 mb-1">Pour SUS-décalage ST :</div>
                      <div className="text-xs text-[hsl(var(--text-dim))]">
                        Si <strong className="text-purple-300">A/B &gt; 0,25</strong> → IDM probable
                      </div>
                      <div className="text-[10px] text-green-400 mt-1">Sensibilité 91%, Spécificité 90%</div>
                    </div>
                  </div>
                </div>

                {/* NOTE IMPORTANTE */}
                <Alert className="bg-cyan-400/10 border-cyan-400/30 p-2">
                  <AlertDescription className="text-xs text-cyan-300">
                    <strong>📝 Note importante :</strong><br/>
                    • En présence d'une clinique évocatrice, la présence d'un de ces 3 critères (B moins spécifique) peut permettre de conclure à un équivalent ST+ en présence d'un BBG<br/>
                    • En cas de <strong>Pacemaker</strong> : seul le critère C est valable (A et B à ne pas utiliser)
                  </AlertDescription>
                </Alert>

                {/* CALCULATEUR RATIO */}
                <Card className="border-cyan-400/30">
                  <CardHeader className="p-2.5">
                    <div className="text-xs font-semibold text-cyan-300">CALCULATEUR RATIO (Critère C)</div>
                  </CardHeader>
                  <CardContent className="p-2.5 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">A (amplitude ST, mm)</label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.amplitudeA || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, amplitudeA: e.target.value }))}
                          placeholder="mm"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">B (amplitude QRS, mm)</label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.amplitudeB || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, amplitudeB: e.target.value }))}
                          placeholder="mm"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>

                    {formData.amplitudeA && formData.amplitudeB && (
                      <div className="space-y-2">
                        <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-400/30">
                          <div className="text-xs font-medium text-purple-300 mb-1">Ratios calculés :</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-[hsl(var(--text-dim))]">B/A = </span>
                              <span className="font-mono font-bold text-purple-300">
                                {(parseFloat(formData.amplitudeB) / parseFloat(formData.amplitudeA)).toFixed(2)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[hsl(var(--text-dim))]">A/B = </span>
                              <span className="font-mono font-bold text-purple-300">
                                {(parseFloat(formData.amplitudeA) / parseFloat(formData.amplitudeB)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {(() => {
                          const ratioBA = parseFloat(formData.amplitudeB) / parseFloat(formData.amplitudeA);
                          const ratioAB = parseFloat(formData.amplitudeA) / parseFloat(formData.amplitudeB);
                          const idmProbableSousDec = ratioBA > 0.30;
                          const idmProbableSusDec = ratioAB > 0.25;
                          
                          if (idmProbableSousDec || idmProbableSusDec) {
                            return (
                              <Alert className="bg-red-500/20 border-red-500/40 p-2">
                                <AlertTriangle className="h-4 w-4 text-red-400" />
                                <AlertDescription className="text-xs text-red-300 font-medium">
                                  ⚠️ CRITÈRE C POSITIF - IDM probable
                                  {idmProbableSousDec && <div className="mt-1">• B/A &gt; 0,30 (sous-décalage)</div>}
                                  {idmProbableSusDec && <div className="mt-1">• A/B &gt; 0,25 (sus-décalage)</div>}
                                </AlertDescription>
                              </Alert>
                            );
                          } else {
                            return (
                              <Alert className="bg-green-500/20 border-green-500/40 p-2">
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                                <AlertDescription className="text-xs text-green-300">
                                  ✓ Critère C négatif - Ratios non significatifs
                                </AlertDescription>
                              </Alert>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5 mt-3">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations SCA ST+
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision, urgence cardiologique
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
